import { last, sortBy, uniqBy } from "lodash-es";
import {
  find,
  getDescendantCount,
  getTreeFromFlatData,
  TreeItem,
  TreeNode,
} from "react-sortable-tree";
import { color } from "../../common/MuiThemeProvider.js";
import { ItemType } from "../../clients/graphqlTypes.js";
import { getDefaultCreatableSubItemTypes } from "../features/doc-items/constraints.js";
import {
  getAllParentItemIds,
  getParentItem,
  isAnyParentMatching,
} from "../trees/helpers.js";
import { AdditionalItemInput, VirtualItem, VirtualTreeItem } from "./types.js";

export const LINE_COLOR = "#e0e0e0";
export const ARROW_COLOR = color.primary;
export const ROOT_ITEM_ID = "root-synthetic-id";

// react-sortable-tree uses this as each row's React key; the default treeIndex
// shifts when rows above expand or collapse, which remounts the row.
export const getTreeNodeKey = ({ node }: TreeNode): string => node.id;

export const getTreeFromItems = <T extends VirtualItem>(
  items: T[],
  rootItem: T | undefined,
  selectedItem: T | null,
  filterItem: ((item: T) => boolean) | undefined,
  enableCreating: boolean,
  isItemExpanded: (itemId: string, defaultValue?: boolean) => boolean,
  documentItemTypes: ItemType[],
  shouldRenderCreateInput?: (item: T) => boolean,
  additionalItemInput?: AdditionalItemInput | null
): TreeItem[] => {
  const selectedItemParent =
    selectedItem && selectedItem.parentId !== null
      ? getParentItem(selectedItem, items)
      : null;
  const additionalItem = additionalItemInput
    ? (items.find(i => i.id === additionalItemInput.itemId) ?? null)
    : null;
  const additionalItemParent =
    additionalItem && additionalItem.parentId !== null
      ? getParentItem(additionalItem, items)
      : null;

  const {
    lastRootItemChild,
    lastSelectedItemChild,
    lastSelectedItemParentChild,
  } = items.reduce(
    (acc, item) => {
      if (rootItem && item.parentId === rootItem.id) {
        if (!acc.lastRootItemChild || item.rank > acc.lastRootItemChild.rank)
          acc.lastRootItemChild = item;
      }
      if (selectedItem && item.parentId === selectedItem.id) {
        if (
          !acc.lastSelectedItemChild ||
          item.rank > acc.lastSelectedItemChild.rank
        )
          acc.lastSelectedItemChild = item;
      }
      if (selectedItemParent && item.parentId === selectedItemParent.id) {
        if (
          !acc.lastSelectedItemParentChild ||
          item.rank > acc.lastSelectedItemParentChild.rank
        )
          acc.lastSelectedItemParentChild = item;
      }
      return acc;
    },
    {
      lastRootItemChild: null as T | null,
      lastSelectedItemChild: null as T | null,
      lastSelectedItemParentChild: null as T | null,
    }
  );

  const canHaveCreateInput = (
    itemType: ItemType,
    documentItemTypes: ItemType[],
    itemHasLinkedTemplate: boolean
  ): boolean => {
    if (itemHasLinkedTemplate) return false;

    return (
      getDefaultCreatableSubItemTypes(itemType, documentItemTypes).length > 0
    );
  };

  const inputItems: VirtualTreeItem<T>[] = enableCreating
    ? uniqBy(
        [
          // Listed first so it wins `uniqBy` on a rank collision with the
          // sibling-create input below (e.g. "add below" the last child),
          // preserving `isAdditionalInput`.
          ...(additionalItemInput &&
          additionalItem &&
          additionalItemParent &&
          !additionalItemParent.deletedAt &&
          additionalItemParent.id !== ROOT_ITEM_ID &&
          shouldRenderCreateInput?.(additionalItemParent) !== false &&
          canHaveCreateInput(
            additionalItemParent.type,
            documentItemTypes,
            "linkedQuoteTemplate" in additionalItemParent &&
              Boolean(additionalItemParent.linkedQuoteTemplate)
          )
            ? [
                {
                  id: `input-${additionalItemParent.id}-${
                    additionalItem.rank +
                    (additionalItemInput.position === "below" ? 0.5 : -0.5)
                  }`,
                  parentId: additionalItemParent.id,
                  type: null,
                  expanded: false,
                  selected: false,
                  isInput: true,
                  isAdditionalInput: true,
                  item: additionalItemParent,
                  rank:
                    additionalItem.rank +
                    (additionalItemInput.position === "below" ? 0.5 : -0.5),
                },
              ]
            : []),
          // input added to root item
          ...(rootItem &&
          !rootItem.deletedAt &&
          rootItem.id !== ROOT_ITEM_ID &&
          shouldRenderCreateInput?.(rootItem) !== false &&
          canHaveCreateInput(rootItem.type, documentItemTypes, false)
            ? [
                {
                  id: `input-${rootItem.id}-${(lastRootItemChild?.rank ?? 0) + 0.5}`,
                  parentId: rootItem.id,
                  type: null,
                  expanded: false,
                  selected: false,
                  isInput: true,
                  item: rootItem,
                  rank: (lastRootItemChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),
          // input added to selected item - able to create children
          ...(selectedItem &&
          !selectedItem.deletedAt &&
          selectedItem.id !== rootItem?.id &&
          shouldRenderCreateInput?.(selectedItem) !== false &&
          canHaveCreateInput(
            selectedItem.type,
            documentItemTypes,
            "linkedQuoteTemplate" in selectedItem &&
              Boolean(selectedItem.linkedQuoteTemplate)
          )
            ? [
                {
                  id: `input-${selectedItem.id}-${(lastSelectedItemChild?.rank ?? 0) + 0.5}`,
                  parentId: selectedItem.id,
                  type: null,
                  expanded: false,
                  selected: false,
                  isInput: true,
                  item: selectedItem,
                  rank: (lastSelectedItemChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),
          // input added to selected parent item - able to create siblings
          ...(selectedItemParent &&
          !selectedItemParent.deletedAt &&
          selectedItemParent.id !== rootItem?.id &&
          shouldRenderCreateInput?.(selectedItemParent) !== false &&
          canHaveCreateInput(
            selectedItemParent.type,
            documentItemTypes,
            "linkedQuoteTemplate" in selectedItemParent &&
              Boolean(selectedItemParent.linkedQuoteTemplate)
          )
            ? [
                {
                  id: `input-${selectedItemParent.id}-${(lastSelectedItemParentChild?.rank ?? 0) + 0.5}`,
                  parentId: selectedItemParent.id,
                  type: null,
                  expanded: false,
                  selected: false,
                  isInput: true,
                  item: selectedItemParent,
                  rank: (lastSelectedItemParentChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),
        ],
        i => i.id
      )
    : [];

  const treeFlatItems: VirtualTreeItem<T>[] = items
    .filter(item => {
      if (filterItem?.(item) === false) return false;
      return true;
    })
    .map(item => ({
      id: item.id,
      type: item.type,
      rank: item.rank,
      parentId: item.parentId ?? null,
      expanded: isItemExpanded(item.id),
      selected: selectedItem?.id === item.id,
      isInput: false,
      item,
    })) as VirtualTreeItem<T>[];

  return getTreeFromFlatData({
    flatData: sortBy(treeFlatItems.concat(inputItems), n => n.rank),
    getKey: item => item.id,
    getParentKey: item => item.parentId,
    rootKey: rootItem?.id,
  });
};

export const getLastSelectedNode = <T extends VirtualItem>(
  treeData: TreeItem[],
  items: T[],
  selectedItem: T | null
): [number | null, string | null, (string | number)[]] => {
  const selectedItemParentIds = (
    selectedItem ? getAllParentItemIds(selectedItem, items) : []
  ).reduce(
    (acc, id, index) => {
      acc[id] = true;
      return acc;
    },
    {} as { [key: string]: boolean }
  );

  const { matches: matchesSelectedNodes } = find({
    getNodeKey: getTreeNodeKey,
    treeData,
    searchMethod: ({ node }) =>
      node.id === selectedItem?.id || !!selectedItemParentIds[node.id],
    searchFocusOffset: 0,
    expandAllMatchPaths: false,
    expandFocusMatchPaths: false,
  });

  const selectedNodes = matchesSelectedNodes.filter(m => m.treeIndex !== null);
  const selectedNode = last(matchesSelectedNodes);
  const lastSelectedNode = last(selectedNodes);

  return [
    lastSelectedNode?.treeIndex ?? null,
    lastSelectedNode?.node.id ?? null,
    selectedNode?.path ?? [],
  ];
};

export const isNodePlacedAfterInput = (
  quoteItems: TreeItem[],
  node: TreeItem,
  {
    prevTreeIndex,
    nextTreeIndex,
    nextPath,
  }: {
    prevTreeIndex: number;
    nextTreeIndex: number;
    nextPath: (string | number)[];
  }
) => {
  const descendants = getDescendantCount({ node });

  const { matches: matchesInput } = find({
    getNodeKey: getTreeNodeKey,
    treeData: quoteItems,
    searchMethod: ({ node }) => node.isInput,
    searchFocusOffset: 0,
    expandAllMatchPaths: false,
    expandFocusMatchPaths: false,
  });

  const isMovedDown = prevTreeIndex < nextTreeIndex;

  const newTreeIndex = nextTreeIndex;

  if (
    matchesInput.some(match => {
      if (match.path.length !== nextPath.length) {
        return false;
      }
      const inputIndex = match.treeIndex;
      if (isMovedDown) {
        return newTreeIndex + descendants === inputIndex;
      } else {
        return newTreeIndex - 1 === inputIndex;
      }
    })
  ) {
    return true;
  }

  return false;
};

export function getNonLeafChildrenIdsRecurively(node: TreeItem): string[] {
  if (node.children === undefined) return [];
  if (typeof node.children === "function") return []; // FIXME

  return [node.id, ...node.children.flatMap(getNonLeafChildrenIdsRecurively)];
}

export const getSyntheticRootItem = (
  //__typename: Exclude<DocItemTypename, "UnnestedQuoteTemplateItem">,
  __typename: "Item",
  syntheticRootItemId: string
): VirtualItem => {
  return {
    __typename,
    id: syntheticRootItemId,
    // docId: "",
    // title: "",
    // description: "",
    type: "section",
    // docType,
    parentId: null,
    deletedAt: null,
    isRootItem: true,
    rank: 0,
    level: 0,
    title: "",
    pathForPdf: "",
    pathSortable: "",
    decisionIsContingentItem: false,
    decisionContingentItemPreselection: null,
    decisionSubitemsPreselection: [],
    decisionBehaviorOfSubitems: "NONE",
    isItemEliminated: false,
    isExplicitHidden: false,
    childrenVisibility: "SHOW_CHILDREN",
    binding: "binding",
    // needsAgreement: false,
    // agreement: "NONE",
    isHidden: false,
    isPriceHidden: false,
    isParentDecisionPreselected: false,
    isParentDecisionNotPreselected: false,
    isParentContingencyNotPreselected: false,
    // pendingChangeAttributes: null,
    // approvalRequired: false,
    // timeTrackingRequired: false,
    // qaApprovalRequired: false,
    // photoApprovalRequired: false,
    // authorOrganisationId: "",
    // originDocId: "",
    // originItemId: "",
    hasChildren: false,
  };
};

export const getOutOfScreen = (
  index: number | null,
  itemHeight: number,
  scrollPosition: number,
  windowHeight: number
) => {
  if (index === null || !windowHeight) {
    return [false, false];
  }
  const aboveScreen = (index + 0.5) * itemHeight < scrollPosition;
  const belowScreen =
    (index + 0.5) * itemHeight > scrollPosition + windowHeight;
  return [aboveScreen, belowScreen];
};

// Shared by every document tree/grid view: drops an eliminated item and its
// subtree. The currently selected item is always kept — so eliminating the item
// you are editing never navigates you away from it — and so is its parent chain,
// because the tree is rebuilt from this flat list by parent id and a node whose
// ancestors were dropped cannot be attached to the root. Its descendants are not.
export function getEliminatedItemsFilter<
  T extends {
    id: string;
    parentId?: string | null;
    isItemEliminated: boolean;
    isAnyParentItemEliminated: boolean;
  },
>(
  areEliminatedItemsShown: boolean,
  selectedItemId: string | null | undefined,
  allItems: T[]
): (item: T) => boolean {
  const selectedItem = selectedItemId
    ? allItems.find(item => item.id === selectedItemId)
    : undefined;

  const alwaysKept = new Set(
    selectedItem
      ? [selectedItem.id, ...getAllParentItemIds(selectedItem, allItems)]
      : []
  );

  return item => {
    if (alwaysKept.has(item.id)) return true;
    if (item.isAnyParentItemEliminated) return false;
    return areEliminatedItemsShown || !item.isItemEliminated;
  };
}

/** Rank that tells the backend to append the item as the last child. */
export const APPEND_RANK = 0;

/**
 * Rank for dropping `movedItemId` directly behind `precedingSiblingId` — or
 * first, when that is `null` — under `newParentId`.
 *
 * A rank is a 1-based insert index among *all* siblings of the new parent once
 * the moved item has been removed from the tree, while drag-and-drop only knows
 * the order of the *visible* siblings: items hidden by the eliminated-items
 * toggle, by search or by client visibility still occupy ranks. Returns `null`
 * when the preceding sibling cannot be found among `allItems`, leaving the
 * fallback to the caller.
 */
export function getRankBehindSibling<
  T extends { id: string; parentId?: string | null; rank: number },
>({
  allItems,
  newParentId,
  movedItemId,
  precedingSiblingId,
}: {
  /** Unfiltered items of the document, so hidden siblings are counted too. */
  allItems: readonly T[];
  newParentId: string;
  movedItemId: string;
  precedingSiblingId: string | null;
}): number | null {
  if (precedingSiblingId === null) return 1;

  const siblings = sortBy(
    allItems.filter(
      item => item.parentId === newParentId && item.id !== movedItemId
    ),
    item => item.rank
  );
  const precedingIndex = siblings.findIndex(
    item => item.id === precedingSiblingId
  );

  return precedingIndex === -1 ? null : precedingIndex + 2;
}

/**
 * Rank for a tree drag-and-drop, given the visible sibling order the drop
 * produced (`visibleSiblingIds` includes the moved item, inputs excluded).
 */
export function getMoveTreeItemRank<
  T extends { id: string; parentId?: string | null; rank: number },
>({
  allItems,
  newParentId,
  movedItemId,
  visibleSiblingIds,
}: {
  allItems: readonly T[];
  newParentId: string;
  movedItemId: string;
  visibleSiblingIds: readonly string[];
}): number {
  const visibleIndex = visibleSiblingIds.indexOf(movedItemId);
  if (visibleIndex < 0) return APPEND_RANK;

  return (
    getRankBehindSibling({
      allItems,
      newParentId,
      movedItemId,
      precedingSiblingId:
        visibleIndex > 0 ? visibleSiblingIds[visibleIndex - 1] : null,
    }) ?? visibleIndex + 1
  );
}

export const isItemVisibleToOtherSide = (
  item: VirtualItem,
  allDocItems: VirtualItem[]
) => {
  return (
    !item.isExplicitHidden &&
    item.type !== "unpaid" &&
    item.binding !== "draft" &&
    !isAnyParentMatching(item, allDocItems, parentItem => {
      return (
        parentItem.isExplicitHidden ||
        parentItem.childrenVisibility === "HIDE_CHILDREN"
      );
    })
  );
};
